import React, { Fragment, useRef, useState } from "react";
import CustomTable from "../../common/CustomTable/CustomTable";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import queryString from "query-string";
import labels from "../../../helpers/labels.json";
import { useSnackbar } from "notistack";
import { getErrorMessage, parseTimeStamp } from "../../../helpers/functions";
import CaseManagerService from "../../../services/CaseManagerService";
import styled from "styled-components";
import moment from "moment";
import { navigate } from "@reach/router";

async function getCMList(query = "") {
  try {
    const response = await CaseManagerService.getCMlist(query);
    return { ...response };
  } catch (error) {
    throw error;
  }
}

const CaseManagerTable = () => {
  const breadcrumbs = [_.startCase(labels.case_manager)];
  const [state, setState] = useState({});
  const MTRef = useRef();
  const { enqueueSnackbar } = useSnackbar();

  const actions = [
    {
      text: labels["add_case_manager"],
      props: {
        onClick: () => navigate(`/dashboard/create-case-manager`),
      },
    },
  ];

  const columns = [
    {
      field: "id",
      title: labels["id_type"],
      sorting: true,
      tooltip: "Unique Identifier for the Scrutiny Clerk across JustAct platform",
      render: (rowData) => (
        <LinkCell onClick={() => navigate(`/dashboard/${rowData?.id}`)}>
          {rowData?.id}
        </LinkCell>
      ),
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
    {
      field: "name",
      title: labels["profile_name"],
      sorting: false,
      render: (rowData) => (
        <BoldCell onClick={() => navigate(`/dashboard/${rowData?.id}`)}>
          {rowData?.name}
        </BoldCell>
      ),
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
    {
      field: "email",
      tooltip: "Email associated with the Scrutiny Clerk",
      title: labels["profile_email"],
      sorting: false,
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
    {
      field: "mobile",
      tooltip: "Phone number associated with the Scrutiny Clerk",
      title: labels["profile_mobile"],
      sorting: false,
      render: (rowData) => (
        <TextCell>{rowData?.mobile ? rowData?.mobile : "-"}</TextCell>
      ),
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
    {
      field: "created_at",
      title: labels["created_on"],
      tooltip: "Scrutiny Clerk Created Date",
      sorting: true,
      render: (rowData) => (
        <TextCell>
          {moment(parseTimeStamp(rowData.created_at)).format("DD/MM/YYYY")}
        </TextCell>
      ),
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
    {
      field: "updated_at",
      title: labels["updated_on"],
      sorting: true,
      tooltip: "Scrutiny Clerk Updated Date",
      render: (rowData) => (
        <TextCell>
          {moment(parseTimeStamp(rowData.updated_at)).format("DD/MM/YYYY")}
        </TextCell>
      ),
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
    {
      field: "actions",
      title: "Actions",
      tooltip: "Option to view Scrutiny Clerk profile",
      sorting: false,
      render: (rowData) => (
        <LinkCell onClick={() => navigate(`/dashboard/${rowData?.id}`)}>
          View
        </LinkCell>
      ),
      cellStyle: {
        padding: "12px 16px",
        textAlign: "left",
      },
      headerStyle: {
        padding: "12px 16px",
        textAlign: "left",
        fontWeight: 600,
        backgroundColor: "#f8f9fc",
        borderBottom: "1px solid #e8eef5",
      },
    },
  ];

  const data = (query) =>
    new Promise((resolve) => {
      let params = {
        page: query.page + 1,
        perPage: 10,
      };
      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }
      if (query?.search) {
        params.query = query?.search;
      }
      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }
      getCMList(stringParams)
        .then(async (result) => {
          if (result) {
            resolve({
              data: result.data,
              page: result.page - 1,
              total: result.total,
            });
            setState(result);
          }
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), {
            variant: "error",
          });
          setState({ data: [], lastPage: 1, page: 1, perPage: 10, total: 0 });
          resolve({
            data: [],
            page: 0,
            total: 0,
          });
        });
    });

  return (
    <Fragment>
      <ActionBar {...{ breadcrumbs }} {...{ actions }} />
      <TableContainer>
        <CustomTable
          hidePagination={state.lastPage === 1}
          pageSize={state?.data?.length ? state?.data?.length : 10}
          pluralTitle={labels.case_manager}
          placeholderText={labels.search_case_manager}
          hideFilterBar
          singularTitle=""
          {...{
            columns,
            data,
            MTRef,
          }}
          state={state}
        />
      </TableContainer>
    </Fragment>
  );
};

export default CaseManagerTable;

const TableContainer = styled.div`
  padding: 24px;
  background-color: #ffffff;

  @media (max-width: 1024px) {
    padding: 16px;
  }

  @media (max-width: 768px) {
    padding: 12px;

    .MuiTableCell-root {
      padding: 8px 12px;
      font-size: 13px;
    }
  }
`;

const LinkCell = styled.span`
  cursor: pointer;
  font-family: openSans-SemiBold, sans-serif;
  color: #00838c;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-block;

  &:hover {
    color: #00838c;
    text-decoration: underline;
  }

  &:active {
    color: #00838c;
  }
`;

const BoldCell = styled.span`
  cursor: pointer;
  font-weight: 600;
  color: #293461;
  transition: color 0.2s ease;
`;

const TextCell = styled.span`
  color: #293461;
  font-weight: 500;
`;
